import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import MemberApi from '../../Api/MemberApi';
import PartnerOpportunitiesApi from '../../Api/PartnerOpportunitiesApi';
import { useAppSelector } from '../../Reducers/store';
import MemberListTable from '../Member/MemberListTable';
import { Member } from '../../Models/Member';
import { opportunityDisplayHeading, PartnerOpportunity } from '../../Models/PartnerOpportunity';
import { ApplicationStatus } from '../../Enums/ApplicationStatus';
import { Button, Label, Radio } from 'flowbite-react';
import { RowSelectionState } from '@tanstack/react-table';

export default function SuperDashboard() {
  const { userModel } = useAppSelector((state) => state.user);
  const [memberListData, setMemberListData] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [applicationStatusId, setApplicationStatusId] = useState<number>(1);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [selectedMembers, setselectedMembers] = useState<Member[]>([]);
  const [pendingOpportunities, setPendingOpportunities] = useState<PartnerOpportunity[]>([]);
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);

  useEffect(() => {
    getMemberApplications();
  }, [userModel, applicationStatusId]);

  const loadPendingPartnerOpportunities = useCallback(() => {
    setOpportunitiesLoading(true);
    PartnerOpportunitiesApi.search({
      keyword: '',
      seekingType: '',
      status: 'Unapproved',
      regions: [],
      states: [],
      categories: [],
      page: 1,
      pageSize: 50,
    })
      .then((res) => setPendingOpportunities(res.data ?? []))
      .catch(() => toast.error('Could not load partner opportunities.'))
      .finally(() => setOpportunitiesLoading(false));
  }, []);

  useEffect(() => {
    if (userModel) {
      loadPendingPartnerOpportunities();
    }
  }, [userModel, loadPendingPartnerOpportunities]);

  const approvePartnerOpportunity = async (id: number) => {
    try {
      const res = await PartnerOpportunitiesApi.moderate(id, { status: 'Published' });
      if (res.data.success) {
        toast.success('Opportunity published.');
        loadPendingPartnerOpportunities();
        return;
      }
      toast.error(res.data.message || 'Could not publish.');
    } catch {
      toast.error('Could not publish.');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setApplicationStatusId(Number.parseInt(value));
  };

  const getMemberApplications = () => {
    try {
      setIsLoading(true);
      return MemberApi.getMembersByStatus(applicationStatusId)
        .then((res) => {
          setMemberListData(res.data);
          setIsLoading(false);
        })
        .catch((err) => {
          toast.error('Error');
          console.error(err);
          setIsLoading(false);
        });
    } catch (error) {
      toast.error('Error');
      console.error(error);
      setIsLoading(false);
    }
  };
  return (
    <>
      <div className='border-2 border-rose-600 rounded-md'>
        <div className='flex'>
          <div className='flex gap-4 absolute p-4 border-2'>
            <div className='flex items-center gap-1'>
              <Radio
                id='reject'
                name='applicationStatus'
                value={ApplicationStatus.All}
                onChange={handleChange}
                checked={applicationStatusId === ApplicationStatus.All}
              />
              <Label htmlFor='reject'>All</Label>
            </div>
            <div className='flex items-center gap-1'>
              <Radio
                id='approve'
                name='applicationStatus'
                value={ApplicationStatus.New}
                onChange={handleChange}
                checked={applicationStatusId === ApplicationStatus.New}
              />
              <Label htmlFor='approve'>New</Label>
            </div>
            <div className='flex items-center gap-1'>
              <Radio
                id='reject'
                name='applicationStatus'
                value={ApplicationStatus.Rejected}
                onChange={handleChange}
                checked={applicationStatusId === ApplicationStatus.Rejected}
              />
              <Label htmlFor='reject'>Rejected</Label>
            </div>
          </div>
          <div className='w-full flex mx-0 justify-end md:justify-center md:mx-auto space-x-2 font-bold text-gray-500 leading-8 py-2'>
            <span className='tracking-wide uppercase text-lg md:text-3xl'>
              Applications
            </span>
          </div>
        </div>
        <div className='w-full p-4'>
          {isLoading && <Loader />}
          <MemberListTable
            data={memberListData}
            showSelection={false}
            setselectedMembers={setselectedMembers}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
          />
        </div>
      </div>

      <div className='border-2 border-rose-600 rounded-md mt-6'>
        <div className='w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-4 pt-4 pb-2'>
          <span className='tracking-wide uppercase text-lg md:text-2xl font-bold text-gray-500'>
            Partner opportunities — pending approval
          </span>
          <div className='flex gap-2'>
            <Button size='xs' color='light' onClick={() => loadPendingPartnerOpportunities()}>
              Refresh
            </Button>
            <Link to='/partner-opportunities'>
              <Button size='xs' className='buttonMain'>
                Full list
              </Button>
            </Link>
          </div>
        </div>
        <div className='w-full p-4 pt-0'>
          {opportunitiesLoading && <Loader />}
          {!opportunitiesLoading && pendingOpportunities.length === 0 && (
            <p className='text-sm text-gray-600 py-2'>No opportunities awaiting approval.</p>
          )}
          {!opportunitiesLoading && pendingOpportunities.length > 0 && (
            <div className='overflow-x-auto'>
              <table className='w-full text-sm text-left'>
                <thead>
                  <tr className='border-b border-gray-200 text-gray-700'>
                    <th className='py-2 pr-3 font-semibold'>Listing</th>
                    <th className='py-2 pr-3 font-semibold'>Member</th>
                    <th className='py-2 pr-3 font-semibold'>Submitted</th>
                    <th className='py-2 pr-3 font-semibold text-right'>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingOpportunities.map((o) => (
                    <tr key={o.id} className='border-b border-gray-100'>
                      <td className='py-2 pr-3 font-medium text-gray-900 max-w-xs'>
                        <span className='line-clamp-2' title={opportunityDisplayHeading(o)}>
                          {opportunityDisplayHeading(o)}
                        </span>
                      </td>
                      <td className='py-2 pr-3 text-gray-700 whitespace-nowrap'>
                        {o.memberName ?? `Member #${o.memberId}`}
                      </td>
                      <td className='py-2 pr-3 text-gray-600 whitespace-nowrap'>
                        {o.dateCreated
                          ? new Date(o.dateCreated).toLocaleDateString()
                          : '—'}
                      </td>
                      <td className='py-2 pr-0 text-right whitespace-nowrap'>
                        <Button
                          size='xs'
                          color='success'
                          className='mr-1'
                          onClick={() => void approvePartnerOpportunity(o.id)}
                        >
                          Approve
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
